import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../theme';
import * as api from '../api';
import { makeAuthStyles, errText } from './authStyles';

const PAGE_SIZE = 10;

// Colour per alert level for the little level chip on each row.
function levelColor(level, C) {
  if (level === 'CRITICAL') return C.altUp;   // red
  if (level === 'WARNING')  return '#ff8c00';  // amber
  if (level === 'INFO')     return C.altDown;  // blue/green accent
  return C.textMuted;
}

// duration_seconds → "m:ss" (or "h:mm:ss" past an hour).
function fmtDuration(sec) {
  if (sec == null) return '';
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

// Stable per-day key + human "d.m.yyyy" label for the section headers.
function dayKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabel(iso) {
  const d = new Date(iso);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

// Group an already-sorted (newest-first) incident list into day sections.
function groupByDay(items) {
  const sections = [];
  let cur = null;
  for (const inc of items) {
    const key = inc.start_time ? dayKey(inc.start_time) : 'unknown';
    if (!cur || cur.key !== key) {
      cur = { key, label: inc.start_time ? dayLabel(inc.start_time) : '—', items: [] };
      sections.push(cur);
    }
    cur.items.push(inc);
  }
  return sections;
}

export default function IncidentsPage() {
  const { user } = useAuth();
  const { colors } = useTheme();
  const C = colors;
  const S = makeAuthStyles(C);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, error: '', items: [], count: 0 });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: '' }));
    (async () => {
      try {
        const res = await api.getMyIncidents(page, PAGE_SIZE);
        const items = Array.isArray(res) ? res : (res?.results || []);
        const count = res?.pagination?.count ?? items.length;
        if (!cancelled) setState({ loading: false, error: '', items, count });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: errText(err), items: [], count: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [page]);

  // Launch the existing replay flow: the ?username=&incident=&view_only=1 deep
  // link that RootRoute renders into TcasScreen (same URL the admin app builds).
  // It goes through RootRoute's deep-link branch, which renders the radar with no
  // auth, so the pilot's own radar token has to travel in the URL like any other
  // deep-link — otherwise the replay endpoints reject it.
  const replay = (inc) => {
    if (!user?.username) return;
    const t = user.radar_token ? `&t=${encodeURIComponent(user.radar_token)}` : '';
    navigate(`/?username=${encodeURIComponent(user.username)}&incident=${inc.id}&autoplay=1&view_only=1${t}`);
  };

  const totalPages = Math.max(1, Math.ceil(state.count / PAGE_SIZE));
  const sections = groupByDay(state.items);

  const rowStyle = (disabled) => ({
    display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', width: '100%',
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '10px 12px', cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1, color: C.textStrong,
  });

  const pagerBtn = (disabled) => ({
    ...S.buttonAlt, width: 'auto', margin: 0, padding: '6px 14px', fontSize: 14,
    opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer',
  });

  return (
    <div style={S.page}>
      <div style={{ ...S.card, position: 'relative', maxWidth: 420 }}>
        <button
          type="button" onClick={() => navigate('/menu')} title="Back to menu" aria-label="Back to menu"
          style={{ ...S.buttonAlt, position: 'absolute', top: 14, left: 14, width: 'auto', margin: 0,
                   padding: '6px 11px', fontSize: 18, lineHeight: 1 }}
        >
          ←
        </button>
        <h1 style={S.title}>Past Incidents</h1>

        {state.loading && <div style={{ ...S.hint, textAlign: 'center' }}>Loading…</div>}
        {!!state.error && <div style={S.error}>{state.error}</div>}
        {!state.loading && !state.error && state.count === 0 && (
          <div style={{ ...S.hint, textAlign: 'center' }}>No past incidents</div>
        )}

        {!state.loading && !state.error && state.count > 0 && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxHeight: '64vh', overflowY: 'auto' }}>
              {sections.map((sec) => (
                <div key={sec.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.5, color: C.textMuted,
                                textTransform: 'uppercase', padding: '0 2px' }}>
                    {sec.label}
                  </div>
                  {sec.items.map((inc) => {
                    const disabled = !inc.scene_count || !inc.first_scene_id;
                    const when = inc.start_time
                      ? new Date(inc.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                      : '—';
                    const callsigns = (inc.conflicting_callsigns || []).join(', ');
                    return (
                      <button
                        key={inc.id} type="button" style={rowStyle(disabled)}
                        onClick={disabled ? undefined : () => replay(inc)} disabled={disabled}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{when}</span>
                          {inc.alert_level && (
                            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.5, color: '#fff',
                                           background: levelColor(inc.alert_level, C), borderRadius: 4, padding: '2px 6px' }}>
                              {inc.alert_level}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>
                          {fmtDuration(inc.duration_seconds)} · {inc.scene_count || 0} frame{inc.scene_count === 1 ? '' : 's'}
                          {inc.is_open ? ' · OPEN' : ''}
                          {callsigns ? ` · ${callsigns}` : ''}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 14 }}>
                <button type="button" style={pagerBtn(page <= 1)} disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
                <span style={{ fontSize: 12, color: C.textMuted }}>Page {page} of {totalPages}</span>
                <button type="button" style={pagerBtn(page >= totalPages)} disabled={page >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
